import styles from '../../pages/NarrativePage/NarrativePage.module.scss';
import cn from 'classnames';
import * as d3 from 'd3';
import { Link } from 'react-router-dom';
import { Loader } from '../Loader/Loader';
import { useTranslation } from 'react-i18next';
import { platformNamesMapping } from '../../utils/platforms';
import { ReactComponent as ArrowIcon } from '../../pages/NarrativesPage/assets/arrow.svg';
import { platformIconsMapping } from '../../utils/platforms';
import { decodeSourceName } from '../../utils/decodeURI';
import { cutString } from '../../utils/cutString';
import { ReactComponent as VerifiedTwitter } from '../../pages/SourcePage/assets/verifiedTwitter.svg';
import Tooltip from '../Tooltip/Tooltip';
import { mapCompromisedEntityTypes, mapBotEntityTypes, mapAffiliationEntityTypes } from '../../utils/actors';
import { normalizeAffiliationType } from '../../utils/affiliationTypes';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { formatNumber, formatWithCustomGigaBillion } from '../../utils/fomatNumbers';


const sortingFieldNames = {
  FOLLOWERS: 'audience',
};

export const NarrativeSourcesTable = ({
  sources,
  type,
  sorting,
  handleSourceSortingClick,
  handleSourceClick,
  isLoading,
  isShare = false,
}) => {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const workspaceConfig = currentUser?.workspace?.config;
  const featureFlags = workspaceConfig?.featureflags;
  const hideRiskActors = featureFlags?.includes('hide_risk_actors');

  let columnsSet = [
    'Actor',
    'Publications',
    'Audience',
    'Origin country',
    'Affiliation',
    'Country of affiliation',
    'Proof of affiliation',
    'Indicators of Risk (IoR)',
    'Proof of IoR',
    'Inauthentic account type',
  ];

  if (hideRiskActors) {
    columnsSet = [
      'Actor',
      'Publications',
      'Audience',
      'Origin country',
      'Affiliation',
      'Country of affiliation',
      'Proof of affiliation',
      'Inauthentic account type',
    ];
  }

  if (type === 'is_bot') {
    columnsSet = ['Actor', 'Publications', 'Audience', 'Origin country', 'Inauthentic account type'];
  }

  if (type === 'state_affiliated') {
    columnsSet = [
      'Actor',
      'Publications',
      'Audience',
      'Origin country',
      'Affiliation',
      'Country of affiliation',
      'Proof of affiliation',
    ];
  }

  if (type === 'discreditated') {
    columnsSet = [
      'Actor',
      'Publications',
      'Audience',
      'Origin country',
      'Indicators of Risk (IoR)',
      'Proof of IoR',
    ];
  }

  const actorInfo = (source) => {
    return (
      <td key={source.id}>
        <div className={styles.sourceNameWrapper}>
          <div className={styles.platformWrapper}>
            {platformIconsMapping[source.source_type]}
          </div>
          <div className={styles.sourceNameContainer}>
            <span
              className={isShare ? styles.nonClickable : undefined}
              {...(!isShare && {
                role: 'button',
                tabIndex: 0,
                onClick: () => handleSourceClick(parseInt(source?.id)),
                onKeyDown: (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSourceClick(parseInt(source?.id));
                  }
                },
              })}
            >
              {cutString(decodeSourceName(source.name), 30)}
              {source?.is_verified ? (
                <span className={styles.verified}>
                  <VerifiedTwitter />
                </span>
              ) : (
                ''
              )}
            </span>
            <a href={source?.url} target="_blank" rel="noopener noreferrer">
              {cutString(decodeSourceName(source?.url), 30)}
            </a>
          </div>
        </div>
      </td>
    );
  };

  const audience = (source) => {
    let followers_cnt = source?.audience;
    return <td key={`${source.id}_${followers_cnt}`}>{formatWithCustomGigaBillion(followers_cnt)}</td>;
  };

  const originCountry = (source) => {
    return <td key={`country_name_${source.id}`}>{source?.origin_country?.name}</td>;
  };

  const affiliation = (source) => {
    return (
      <td key={`affiliation_${source.id}`}>
        {source.affiliations?.length > 0 ? (
          normalizeAffiliationType(source?.affiliations[0].entity_type) ===
          'STATE_LINKED_CHANNELS' ? (
            <Tooltip
              content={t(
                'The source is reported to be state affiliated by an authoritative organization (government or non-government)',
              )}
              position="bottom"
            >
              {mapAffiliationEntityTypes(t)[source?.affiliations[0].entity_type]}
            </Tooltip>
          ) : (
            mapAffiliationEntityTypes(t)[source?.affiliations[0].entity_type]
          )
        ) : (
          ''
        )}
      </td>
    );
  }; 

  const affiliationCountry = (source) => {
    return(
      <td key={`affiliation_country_${source.id}`}>
        {source.affiliations?.length > 0
          ? source.affiliations[0]?.country?.name
          : ''}
      </td>
    )
  }

  const affiliationProof = (source) => {
    const proof = source?.affiliations?.[0];
    const isUkLocale =
      localStorage.getItem('i18nextLng') === 'ua-UK' ||
      localStorage.getItem('i18nextLng') === 'ru-RU' ||
      localStorage.getItem('i18nextLng') === 'ru' ||
      localStorage.getItem('i18nextLng') === 'uk';
    // fall back to the other language so a link never renders with empty text
    const affilliationProofName = isUkLocale
      ? proof?.proof_name_uk || proof?.proof_name_en
      : proof?.proof_name_en || proof?.proof_name_uk;
    const proofLink =
      proof?.proof_link === 'Twitter labeling'
        ? 'https://help.twitter.com/en/rules-and-policies/government-media-labels#:~:text=State%2Daffiliated%20media%20is%20defined,control%20over%20production%20and%20distribution'
        : proof?.proof_link;

    return (
      <td key={`affiliation_proof_${source.id}`}>
        {proofLink ? (
          <a href={proofLink} target="_blank" rel="noopener noreferrer">
            {affilliationProofName || proof.proof_link}
          </a>
        ) : (
          affilliationProofName || ''
        )}
      </td>
    );
  };

  const discreditation = (source) => {
    return (
      <td key={`discreditation_${source.id}`}>
        <Tooltip
          content={
            mapCompromisedEntityTypes(t)[source.discreditations[0]?.entity_type]
              ?.tooltip || ''
          }
          position="bottom"
        >
          {
            mapCompromisedEntityTypes(t)[source.discreditations[0]?.entity_type]
              ?.title
          }
        </Tooltip>
      </td>
    );
  };

  const discreditationProof = (source) => {
    const proof = source?.discreditations?.[0];
    const isUkLocale =
      localStorage.getItem('i18nextLng') === 'ua-UK' ||
      localStorage.getItem('i18nextLng') === 'ru-RU' ||
      localStorage.getItem('i18nextLng') === 'ru' ||
      localStorage.getItem('i18nextLng') === 'uk';
    // fall back to the other language so a link never renders with empty text
    const discreditationProofName = isUkLocale
      ? proof?.proof_name_uk || proof?.proof_name_en
      : proof?.proof_name_en || proof?.proof_name_uk;

    return (
      <td key={`discreditation_proof_${source.id}`}>
        {proof?.proof_link ? (
          <a href={proof.proof_link} target="_blank" rel="noopener noreferrer">
            {discreditationProofName || proof.proof_link}
          </a>
        ) : (
          discreditationProofName || ''
        )}
      </td>
    );
  };

  const botType = (source) => {
    let entityType = t('N/A');

    if (source?.suspended_by_platform === true) {
      entityType = mapBotEntityTypes(t)['suspended_by_platform']?.title;
    } else if (
      source?.inauthentic_behavior === true ||
      source?.is_bot === true
    ) {
      entityType = mapBotEntityTypes(t)['inauthentic_behavior']?.title;
    } else if (source?.immature_account === true) {
      entityType = mapBotEntityTypes(t)['immature_account']?.title;
    }
    return <td key={`bot_type_${source.id}`}>{entityType}</td>;
  };

  let columnsSetMap = {
    'Actor': actorInfo,
    'Publications': (source) => <td key={`messages_${source.id}`}>{formatNumber(source?.messages_count)}</td>,
    'Audience': audience,
    'Origin country': originCountry,
    'Affiliation': affiliation,
    'Country of affiliation': affiliationCountry,
    'Proof of affiliation': affiliationProof, 
    'Indicators of Risk (IoR)': discreditation, 
    'Proof of IoR': discreditationProof, 
    'Inauthentic account type': botType,
  }

  return (
    <>
      {isLoading || sources === null ? (
        <Loader />
      ) : sources?.objects?.length > 0 ? (
        <div className={styles.tableWrapper}>
          <div>
            <table className={styles.sources} id='widgetContentModal'>
              <thead>
                <tr className={styles.headerInfo}>
                  <td>
                    {t('Total actors')}: {formatNumber(sources.total)}
                  </td>
                  <td colSpan="11"></td>
                </tr>
                <tr>
                  {columnsSet.map((name) => {
                    return (
                      <td key={name}>
                        <div className={styles.wrapper}>
                          <span>{t(name)}</span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {sources?.objects?.map((source) => {
                  return (
                    <tr key={source.id}>
                      {columnsSet.map((column) => {
                        return columnsSetMap[column](source);
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className={styles.noData}>{t('There is no data to display')}</div>
      )}
    </>
  );
};
